// License client for the paid product (D-017: one yearly subscription,
// login required). Server contract: services/api/DESIGN.md.
//
// The gate is ENABLED (D-140 launch): login required, verified against the
// live backend. Escape hatches for development, via storage overrides:
//   "licenseGateEnabled": false  → bypass the gate entirely
//   "licenseApiBase": "dev"      → accept any email/key login (offline)
// The founder runs a permanent comped license (active, far-future) so the
// real login path is exercised in day-to-day dev without ever lapsing.
//
// Non-negotiable rider (D-017): a lapsed subscription NEVER blocks the
// trader's own data — the lapse screen keeps the journal readable and
// the one-file export working, forever.

import { storageGet, storageSet, onStorageChange, storageRemove } from "./storage.js";

const GATE_DEFAULT_ENABLED = true;
// Live backend on its custom domain (tickahead.com zone on Cloudflare).
const DEFAULT_API_BASE = "https://api.tickahead.com";
/** How long a last-known-good "active" survives without reaching the
 * server (travel, weekends, our outage — never punish the user for it). */
export const OFFLINE_GRACE_MS = 72 * 3600 * 1000;
/** Re-verify against the server at most this often. */
const RECHECK_INTERVAL_MS = 12 * 3600 * 1000;

export type LicenseState = "active" | "lapsed" | "unlicensed";

export interface StoredLicense {
  email: string;
  licenseKey: string;
  token: string;
  status: "active" | "past_due" | "canceled" | "lapsed";
  renewsAt: number | null; // unix seconds
  checkedAt: number; // ms — last successful server confirmation
}

const LICENSE_KEY = "license";

/** Pure state resolution — unit-tested in tests/license.test.ts. */
export function resolveLicenseState(license: StoredLicense | null, now: number): LicenseState {
  if (!license || !license.email || !license.token) return "unlicensed";
  if (license.status === "active" || license.status === "past_due") {
    return now - license.checkedAt <= OFFLINE_GRACE_MS ? "active" : "lapsed";
  }
  return "lapsed";
}

export async function licenseGateEnabled(): Promise<boolean> {
  return await storageGet<boolean>("licenseGateEnabled", GATE_DEFAULT_ENABLED);
}

export async function apiBase(): Promise<string> {
  return await storageGet<string>("licenseApiBase", DEFAULT_API_BASE);
}

export async function getStoredLicense(): Promise<StoredLicense | null> {
  return await storageGet<StoredLicense | null>(LICENSE_KEY, null);
}

export async function licenseState(): Promise<LicenseState> {
  return resolveLicenseState(await getStoredLicense(), Date.now());
}

export interface LoginResult {
  ok: boolean;
  lapsed?: boolean;
  message?: string;
}

export async function login(email: string, licenseKey: string): Promise<LoginResult> {
  const base = await apiBase();
  const cleanEmail = email.trim().toLowerCase();
  const cleanKey = licenseKey.trim().toUpperCase();
  if (!cleanEmail || !cleanKey) return { ok: false, message: "Enter your email and license key." };

  // Dev mode ("licenseApiBase": "dev"): accept any login so the product
  // can be exercised before the backend is deployed.
  if (base === "dev") {
    await storageSet<StoredLicense>(LICENSE_KEY, {
      email: cleanEmail,
      licenseKey: cleanKey,
      token: "dev-token",
      status: "active",
      renewsAt: null,
      checkedAt: Date.now(),
    });
    return { ok: true };
  }

  try {
    const res = await fetch(`${base}/v1/auth/login`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: cleanEmail, licenseKey: cleanKey }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      token?: string;
      status?: string;
      renewsAt?: number | null;
      error?: string;
    };
    if (res.ok && body.token) {
      await storageSet<StoredLicense>(LICENSE_KEY, {
        email: cleanEmail,
        licenseKey: cleanKey,
        token: body.token,
        status: (body.status as StoredLicense["status"]) ?? "active",
        renewsAt: body.renewsAt ?? null,
        checkedAt: Date.now(),
      });
      return { ok: true };
    }
    if (res.status === 403) {
      await storageSet<StoredLicense>(LICENSE_KEY, {
        email: cleanEmail,
        licenseKey: cleanKey,
        token: "",
        status: "lapsed",
        renewsAt: body.renewsAt ?? null,
        checkedAt: Date.now(),
      });
      return { ok: false, lapsed: true, message: "That subscription has ended — renew at tickahead.com and sign in again." };
    }
    return { ok: false, message: body.error === "email and licenseKey required" ? "Enter your email and license key." : "No license found for that email and key — check both against your welcome email." };
  } catch {
    return { ok: false, message: "Can't reach TickAhead right now — check your connection and try again." };
  }
}

/** Forgot-your-key recovery (D-144): ask the server to email the license key to
 * the address on file. Always resolves to the same neutral message (the server
 * never reveals whether an email is a customer), so treat any resolution as
 * "we've sent it if it exists." */
export async function recoverKey(email: string): Promise<{ ok: boolean; message: string }> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes("@")) {
    return { ok: false, message: "Enter the email you bought TickAhead with." };
  }
  const base = await apiBase();
  const sent = { ok: true, message: "If that email has a license, we've emailed your key. Check your inbox." };
  if (base === "dev") return sent;
  try {
    await fetch(`${base}/v1/license/recover`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: cleanEmail }),
    });
    return sent;
  } catch {
    return { ok: false, message: "Can't reach TickAhead right now — check your connection and try again." };
  }
}

/** Magic-link sign-in, step 1 (D-151): ask the server to email a one-time
 * sign-in link. Neutral message either way (never reveals who's a customer). */
export async function requestMagicLink(email: string): Promise<{ ok: boolean; message: string }> {
  const cleanEmail = email.trim().toLowerCase();
  if (!cleanEmail || !cleanEmail.includes("@")) {
    return { ok: false, message: "Enter the email you subscribed with." };
  }
  const base = await apiBase();
  const sent = { ok: true, message: "Check your inbox — if that email has a subscription, a sign-in link is on its way." };
  if (base === "dev") return sent;
  try {
    await fetch(`${base}/v1/auth/request-link`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email: cleanEmail }),
    });
    return sent;
  } catch {
    return { ok: false, message: "Can't reach TickAhead right now — check your connection and try again." };
  }
}

/** Magic-link sign-in, step 2 (D-151): exchange the link's token for a session.
 * Stores email + key + token exactly like a key login, so persistence/refresh
 * behave identically (the user just never had to type the key). */
export async function signInWithMagicToken(token: string): Promise<LoginResult> {
  const base = await apiBase();
  if (base === "dev") {
    await storageSet<StoredLicense>(LICENSE_KEY, { email: "dev@tickahead.com", licenseKey: "DEV", token: "dev-token", status: "active", renewsAt: null, checkedAt: Date.now() });
    return { ok: true };
  }
  try {
    const res = await fetch(`${base}/v1/auth/verify-link`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    const body = (await res.json().catch(() => ({}))) as {
      token?: string; status?: string; renewsAt?: number | null; email?: string; licenseKey?: string; error?: string;
    };
    if (res.ok && body.token && body.email) {
      await storageSet<StoredLicense>(LICENSE_KEY, {
        email: body.email,
        licenseKey: body.licenseKey ?? "",
        token: body.token,
        status: (body.status as StoredLicense["status"]) ?? "active",
        renewsAt: body.renewsAt ?? null,
        checkedAt: Date.now(),
      });
      return { ok: true };
    }
    if (res.status === 403) {
      return { ok: false, lapsed: true, message: "That subscription has ended — renew at tickahead.com and sign in again." };
    }
    return { ok: false, message: body.error === "link_invalid_or_expired" ? "That sign-in link has expired — request a new one." : "Couldn't sign you in with that link." };
  } catch {
    return { ok: false, message: "Can't reach TickAhead right now — check your connection and try again." };
  }
}

/** Re-confirms the stored license against the server if it's due.
 * Network failures leave the stored state untouched (grace applies). */
export async function refreshLicenseStatus(force = false): Promise<void> {
  const license = await getStoredLicense();
  if (!license || !license.token) return;
  if (!force && Date.now() - license.checkedAt < RECHECK_INTERVAL_MS) return;
  const base = await apiBase();
  if (base === "dev") {
    await storageSet<StoredLicense>(LICENSE_KEY, { ...license, checkedAt: Date.now() });
    return;
  }
  try {
    const res = await fetch(`${base}/v1/license/status`, { headers: { authorization: `Bearer ${license.token}` } });
    if (res.ok) {
      const body = (await res.json()) as { status?: string; renewsAt?: number | null };
      await storageSet<StoredLicense>(LICENSE_KEY, {
        ...license,
        status: (body.status as StoredLicense["status"]) ?? "active",
        renewsAt: body.renewsAt ?? license.renewsAt,
        checkedAt: Date.now(),
      });
    } else if (res.status === 401) {
      // Token expired — silently re-login with the stored credentials.
      await login(license.email, license.licenseKey);
    } else if (res.status === 403) {
      await storageSet<StoredLicense>(LICENSE_KEY, { ...license, status: "lapsed", checkedAt: Date.now() });
    }
  } catch {
    // Offline / our outage: keep last-known-good, grace window covers it.
  }
}

export async function signOut(): Promise<void> {
  await storageRemove(LICENSE_KEY);
}

/** URL of a Stripe customer-portal session for subscription management
 * (card, cancel, invoices). Null when unavailable (dev mode, offline). */
export async function billingPortalUrl(): Promise<string | null> {
  const license = await getStoredLicense();
  if (!license?.token) return null;
  const base = await apiBase();
  if (base === "dev") return null;
  try {
    const res = await fetch(`${base}/v1/billing/portal`, {
      method: "POST",
      headers: { authorization: `Bearer ${license.token}` },
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { url?: string };
    return body.url ?? null;
  } catch {
    return null;
  }
}

export function onLicenseChange(callback: (state: LicenseState) => void): () => void {
  return onStorageChange<StoredLicense | null>(LICENSE_KEY, (value) => {
    callback(resolveLicenseState(value ?? null, Date.now()));
  });
}
